import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

const TOURS = {
  calldesk: [
    ["[data-testid=agent-status]", "Your status", "Set Available to take calls. Away or Offline stops inbound calls ringing you."],
    ["[data-testid=campaign-picker]", "Pick a campaign", "Every outbound list lives in a campaign. Pick one to load your queue."],
    ["[data-testid=dial-panel]", "Up next", "The next business in the queue. Clicking a row only selects it — it never dials."],
    ["[data-testid=dialer-call-next]", "Call / Next", "This is what places a call. During training, don't press it — live calling unlocks after certification and manager approval."],
    ["[data-testid=dialer-skip]", "Skip", "Skip a business you shouldn't call right now (for example, outside their hours)."],
    ["[data-testid=call-wrap]", "Outcome & notes", "After every call: pick an outcome, write notes, set a callback, then Save. (Appears during and after a call.)"],
    ["[data-testid=transfer-btn]", "Transfers", "Hand the caller to a teammate: instant, warm or three-way. (Appears on a live call.)"],
    ["[data-testid=cc-open-voicemail]", "Voicemail", "Your voicemail inbox and greeting."],
    ["[data-testid=cc-new-customer]", "New customer", "Add someone who called in. Search first to avoid duplicates."],
    ["[data-testid=dial-keypad]", "Dial pad", "Type any number and press Call. During a call it sends keypad tones for phone menus. Hide it from the Layout menu or the Hide link."],
    ["[data-testid=cc-open-more]", "More: scripts & tools", "Send info, marketing tools and extras live here."],
    ["[data-testid=cc-stats-toggle]", "My stats", "Your calls, connects, talk time and outcomes."],
  ],
  campaigns: [
    ["[data-testid=campaign-picker]", "Campaign picker", "Switch between campaigns here. Each campaign is one call list with one goal."],
    ["[data-testid=cc-manager-menu]", "Manager menu", "Team dialer, Call Desk management, scripts and team performance. Team dialer: pick the campaign, tick approved reps, Start — each rep then presses Start on their own dialer."],
    ["[data-testid=cc-open-team]", "Callbacks", "See who is online, on a call, and the callbacks that are due."],
    ["[data-testid=cc-open-history]", "Call history", "Review every call, outcome and note to coach your team."],
    ["[data-testid=cc-layout-menu]", "Layout", "Switch fixed or movable panels, pick an arrangement, and show or hide the dial pad."],
    ["[data-testid=cc-stats-toggle]", "Stats", "Calls, talk time and outcome breakdowns."],
  ],
};

const useRect = (sel) => {
  const [r, setR] = useState(null);
  useEffect(() => {
    let first = true;
    const tick = () => { const el = [...document.querySelectorAll(sel)].find(e => e.offsetParent !== null); if (el && first) { el.scrollIntoView({ block: "center", behavior: "smooth" }); first = false; } setR(el ? el.getBoundingClientRect() : null); };
    tick(); const t = setInterval(tick, 300); return () => clearInterval(t);
  }, [sel]);
  return r;
};

export const Tour = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const q = new URLSearchParams(loc.search);
  const steps = TOURS[q.get("tour")];
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [q.get("tour")]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = steps?.[i];
  const r = useRect(step?.[0] || "#__none");
  if (!steps) return null;
  const close = () => { const n = new URLSearchParams(loc.search); ["tour", "course", "lesson"].forEach(k => n.delete(k)); nav({ search: n.toString() }, { replace: true }); };
  const finish = async () => {
    const c = q.get("course"), l = q.get("lesson");
    if (c && l) { try { await api.post(`/api/academy/courses/${c}/lessons/${l}/complete`, {}); toast.success("Tour complete"); nav(`/academy/course/${c}?lesson=${l}`); return; } catch (e) { toast.error(errMsg(e)); } }
    close();
  };
  const pad = 6;
  const card = r ? { top: Math.min(window.innerHeight - 220, r.bottom + 14), left: Math.max(12, Math.min(window.innerWidth - 352, r.left)) } : { top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 170 };
  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="tour">
      {r ? <div className="absolute rounded-xl ring-4 ring-amber-400 transition-all duration-300" style={{ top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2, boxShadow: "0 0 0 9999px rgba(2,6,23,.55)" }} data-testid="tour-highlight" />
        : <div className="absolute inset-0 bg-slate-950/55" />}
      <div className="absolute w-[340px] bg-white rounded-2xl shadow-2xl p-5 pointer-events-auto transition-all duration-300" style={card} data-testid="tour-card">
        <button onClick={close} className="absolute top-3 right-3 text-slate-400 hover:text-slate-900" data-testid="tour-close"><X className="w-4 h-4" /></button>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600">Step {i + 1} of {steps.length}</p>
        <p className="font-extrabold text-lg text-slate-900 mt-1" data-testid="tour-step-title">{step[1]}</p>
        <p className="text-sm text-slate-600 mt-1">{step[2]}</p>
        {!r && <p className="text-xs text-slate-400 mt-2" data-testid="tour-not-visible">This control isn't on screen right now — it shows up in the situation described.</p>}
        <div className="flex gap-2 mt-4">{i > 0 && <button onClick={() => setI(i - 1)} className="h-9 px-4 rounded-full border text-sm font-semibold" data-testid="tour-back">Back</button>}
          {i < steps.length - 1 ? <button onClick={() => setI(i + 1)} className="h-9 px-5 rounded-full bg-slate-900 text-white text-sm font-bold ml-auto" data-testid="tour-next">Next</button>
            : <button onClick={finish} className="h-9 px-5 rounded-full bg-amber-500 text-slate-900 text-sm font-bold ml-auto" data-testid="tour-finish">Finish tour</button>}</div>
      </div>
    </div>
  );
};
